import {
  type ProviderResource,
  type ProviderMembershipResource,
  type ProviderActivePtinStatusResource,
  type ProviderStatusResource,
  type ProviderTypeResource,
} from '@/features/iam/api/resources/provider'
import type {
  ProviderMembership,
  ProviderProps,
  ProviderPtinActiveStatus,
  ProviderStatus,
  ProviderType,
} from '@/features/iam/providers/Provider'
import { maybeParseDate } from '@/utils/date-util'
import { match } from '@/utils/pattern-match-util'

export function mapProviderResourceToProps(
  resource: Partial<ProviderResource>,
): Partial<ProviderProps> {
  return {
    userPublicId: resource.user_public_id,
    givenName: resource.given_name,
    familyName: resource.family_name,
    email: resource.email,
    phone: resource.phone,
    picture: resource.picture,
    workspaces:
      resource.workspaces &&
      mapProviderMembershipResourcesToMemberships(resource.workspaces),
    globalJobCapacity: resource.global_job_capacity,
    softwareExperience: resource.software_experience,
    softwareOwned: resource.software_owned,
    supplementalSoftwareExperience: resource.supplemental_software_experience,
    yearsExperience: resource.years_experience,
    stateRegion: resource.state_region,
    activePtin: resource.active_ptin
      ? mapProviderActivePtinStatusResourceToStatus(resource.active_ptin)
      : 'NotApplicable',
    providerStatus:
      resource.provider_status &&
      mapProviderStatusResourceToProviderStatus(resource.provider_status),
    specialties: resource.specialties,
    dateCreated: maybeParseDate(resource.date_created),
    dateModified: maybeParseDate(resource.date_modified),
    proManager: resource.pro_manager,
    proType: resource.pro_type
      ? mapProviderTypeResourceToProviderType(resource.pro_type)
      : 'NotApplicable',
  }
}

function mapProviderTypeResourceToProviderType(
  resource: ProviderTypeResource,
): ProviderType {
  return match(resource, {
    CPA: 'CPA',
    EA: 'EA',
    NOT_APPLICABLE: 'NotApplicable',
  })
}

export function mapProviderActivePtinStatusResourceToStatus(
  resource: ProviderActivePtinStatusResource,
): ProviderPtinActiveStatus {
  return match(resource, {
    ACTIVE: 'Active',
    INACTIVE: 'Inactive',
    NOT_APPLICABLE: 'NotApplicable',
  })
}

function mapProviderMembershipResourcesToMemberships(
  resources: ProviderMembershipResource[],
): ProviderMembership[] {
  return resources.map((pm) => ({
    workspaceId: pm.workspace_id,
    jobCapacity: pm.job_capacity,
  }))
}

function mapProviderStatusResourceToProviderStatus(
  resource: ProviderStatusResource,
): ProviderStatus {
  return match(resource, {
    ACTIVE: 'Active',
    DECOMMISSIONED: 'Decommissioned',
    SUSPENDED: 'Suspended',
    ON_LEAVE: 'OnLeave',
    NULL: 'Null',
  })
}
